import type { Result } from 'true-myth';

import type { JobProfileTaskTransport } from '~/shared/data-contract/profile-task';
import { jobProfileTaskTransportSchema } from '~/shared/data-contract/profile-task';
import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

export type GenerateProfileTaskInput = {
    readonly acceptLanguage: string;
    readonly architectureId: string;
    readonly itemId: string;
    readonly signal?: AbortSignal;
    readonly title: string;
};

export type RemoveProfileTaskInput = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly taskId: string;
};

export type ProfileTaskFetchers = {
    /**
     * Title-first async task creation: the backend answers 202 with the queued title-only card and
     * generates the content asynchronously (completion arrives via the JSA event stream).
     */
    readonly generateProfileTask: (
        input: GenerateProfileTaskInput,
    ) => Promise<Result<JobProfileTaskTransport, HttpFetcherError>>;
    readonly removeProfileTask: (input: RemoveProfileTaskInput) => Promise<Result<unknown, HttpFetcherError>>;
};

export type ProfileTaskFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createProfileTaskFetchers = (dependencies: ProfileTaskFetchersDependencies): ProfileTaskFetchers => ({
    generateProfileTask: ({ acceptLanguage, architectureId, itemId, signal, title }) =>
        dependencies.validatingCobrainerApiFetcher.postJson(jobProfileTaskTransportSchema, {
            pathname: `/job-architectures/${architectureId}/items/${itemId}/tasks`,
            payload: { title },
            requestOptions: {
                headers: { 'Accept-Language': acceptLanguage },
                signal,
            },
        }),
    removeProfileTask: ({ architectureId, itemId, taskId }) =>
        dependencies.cobrainerApiFetcher.delete({
            pathname: `/job-architectures/${architectureId}/items/${itemId}/tasks/${taskId}`,
        }),
});
